import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { getUserFiles, uploadFile, getFileIcon, formatFileSize, formatDate, handleFileClick } from '../utils/fileOperations';

const FilesTab = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]); // Track uploading files
  const fileInputRef = useRef(null);
  const hasLoadedInitialFiles = useRef(false);

  // Get user email from localStorage
  const getUserEmail = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.email;
    }
    return null;
  };

  // Fetch files when component mounts (with smart caching)
  useEffect(() => {
    let isMounted = true;

    const fetchFiles = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        setError(null);

        const userEmail = getUserEmail();
        if (!userEmail) {
          console.warn('No user email found, skipping file fetch');
          if (isMounted) setFiles([]);
          return;
        }

        console.log('FilesTab: Fetching files...');
        const userFiles = await getUserFiles(userEmail);
        if (isMounted) {
          console.log('FilesTab: Fetched files:', userFiles);
          setFiles(userFiles);
        }
      } catch (err) {
        console.error('Error fetching files:', err);
        if (isMounted) {
          setError('Failed to load files');
          setFiles([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFiles();
    hasLoadedInitialFiles.current = true;

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let uploadingFile = null;

    try {
      setUploading(true);
      setError(null);

      // Create optimistic file entry
      const icon = getFileIcon(file.name);
      uploadingFile = {
        id: `uploading-${Date.now()}`,
        name: file.name,
        type: icon.icon.toLowerCase(),
        icon: icon.icon,
        iconColor: icon.color,
        size: file.size,
        modified: Date.now(),
        url: null,
        isUploading: true,
        uploadProgress: 0,
        uploadStatus: 'Preparing...'
      };

      // Add to uploading files immediately
      setUploadingFiles([uploadingFile]);

      const userEmail = getUserEmail();
      if (!userEmail) {
        throw new Error('User email not found');
      }

      await uploadFile(file, userEmail, (progress, status) => {
        // Update progress in the uploading file
        setUploadingFiles(prev => prev.map(f =>
          f.id === uploadingFile.id
            ? { ...f, uploadProgress: progress, uploadStatus: status }
            : f
        ));
      });

      // Convert uploading file to completed file
      setUploadingFiles(prev => prev.map(f =>
        f.id === uploadingFile.id
          ? { ...f, isUploading: false, uploadProgress: 100, uploadStatus: 'Completed!' }
          : f
      ));

      // FIXED: Remove uploading file immediately before refresh to prevent UI delay
      setUploadingFiles(prev => prev.filter(f => f.id !== uploadingFile.id));
      
      // Refresh files to get the final file data (with URL, etc.)
      const userFiles = await getUserFiles(userEmail);
      setFiles(userFiles);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      // Remove from uploading files on error
      if (uploadingFile) {
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadingFile.id));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Transform backend file data to display format
  const transformFileData = (file) => {
    const icon = getFileIcon(file.original_filename || 'unknown');

    return {
      id: file.original_url || Math.random().toString(), // Use URL as ID or fallback
      name: file.original_filename || 'Unknown File',
      type: icon.icon.toLowerCase(),
      icon: icon.icon,
      iconColor: icon.color,
      size: file.file_size || 0,
      modified: file.uploaded_at || new Date().toISOString(),
      url: file.original_url,
      chunks: file.chunks || []
    };
  };

  const displayFiles = files.map(transformFileData);
  const allFiles = [...uploadingFiles, ...displayFiles]; // Show uploading files first

  if (loading) {
    return (
      <div className="files-loading">
        <div className="loading-spinner"></div>
        <span style={{ fontSize: '60%' }}>Loading files...</span>
      </div>
    );
  }

  return (
    <div className="files-tab-container">
      {/* Error Message */}
      {error && (
        <motion.div
          className="files-error"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </motion.div>
      )}

      {/* Files List */}
      <div className="work-files-list">
        {allFiles.length === 0 ? (
          <div className="files-empty-state">
            <div className="empty-icon">📁</div>
            <h3>No files uploaded yet</h3>
            <p>Upload your first file to get started</p>
          </div>
        ) : (
          allFiles.map((file) => (
            <motion.button
              key={file.id}
              className={`work-file-item ${file.isUploading ? 'uploading' : ''}`}
              whileHover={{ backgroundColor: file.isUploading ? 'transparent' : '#f8f9fa' }}
              type="button"
              onClick={() => {
                console.log('FilesTab: File button clicked!', file.name);
                handleFileClick(file);
              }}
              disabled={file.isUploading}
            >
              <div
                className="work-file-icon"
                style={{
                  background: file.isUploading
                    ? `${file.iconColor}10`
                    : `${file.iconColor}15`,
                  border: `1px solid ${file.iconColor}20`,
                  color: file.iconColor,
                  opacity: file.isUploading ? 0.6 : 1
                }}
              >
                {file.isUploading ? (
                  <div className="upload-progress-circle">
                    <svg className="progress-ring" viewBox="0 0 36 36">
                      <path
                        className="progress-ring-circle"
                        stroke={file.iconColor}
                        strokeWidth="2"
                        fill="transparent"
                        strokeDasharray={`${(file.uploadProgress || 0) * 1.13}, 113`}
                        strokeDashoffset="0"
                      />
                    </svg>
                    <span className="progress-percent">{Math.round(file.uploadProgress || 0)}%</span>
                  </div>
                ) : (
                  <span className="work-file-type">{file.icon}</span>
                )}
              </div>
              <div className="work-file-details">
                <span className={`work-file-name ${file.isUploading ? 'uploading' : ''}`}>
                  {file.name}
                </span>
                <div className="work-file-meta">
                  {file.isUploading ? (
                    <span className="work-file-status">{file.uploadStatus || 'Uploading...'}</span>
                  ) : (
                    <>
                      <span className="work-file-size">{formatFileSize(file.size)}</span>
                      <span className="work-file-modified">{formatDate(file.modified)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="work-file-actions">
                {file.isUploading ? (
                  <div className="upload-spinner"></div>
                ) : (
                  <span className="work-file-action">⋮</span>
                )}
              </div>
            </motion.button>
          ))
        )}

        {/* Upload Button */}
        <button
          className="work-file-add"
          type="button"
          onClick={handleUploadClick}
          disabled={uploading}
        >
          <span>+ Upload File</span>
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.svg,.fig,.sketch,.psd,.ai,.zip,.rar,.7z,.tar,.gz,.mp4,.avi,.mov,.wmv,.flv,.mp3,.wav,.flac,.aac,.js,.ts,.html,.css,.py,.json,.xml"
      />
    </div>
  );
};

export default FilesTab;
